import { godotNodePaths } from '../../../resources/formats/glb/godotNodes';
import { boolSlotValue } from '../../../godot/variant/variantBool.js';
import { decomposeForR3F } from '../../nodeTransform';
import { writeNodeTransform } from './glbNodeTransform';
import { relativeTo } from '../../../utils/nodePath';
import { warnOnce } from '../../../utils/warnOnce';
import { glbFrames } from './glbPathLookup';
/** The drive properties of each frame kind, which change the drawing. A key ending in `/` is a prefix. */
const UNREAD_PROPERTIES = {
    skeleton: ['show_rest_only', 'motion_scale', 'bones/'],
    boneAttachment: ['bone_name', 'bone_idx', 'override_pose', 'use_external_skeleton', 'external_skeleton'],
};
/**
 * Writes the properties of `override` on `frame`, and warns for the ones nothing reads. Returns
 * whether it wrote a transform.
 */
export function applyGlbFrameOverride(entries, frame, override) {
    warnUnreadProperties(entries, frame, override);
    if (boolSlotValue(override.rawProperties.visible) === false) {
        for (const object of objectsBelowFrame(entries, frame))
            object.visible = false;
    }
    const transform = override.properties.transform;
    if (!transform)
        return false;
    if (frame.kind === 'boneAttachment') {
        warnOnce(entries, `${frame.path}\0transform`, `[glbFrameOverrides] "${frame.path}" sets transform, which Godot replaces with the pose of its bone (bone_attachment_3d.cpp:311): it is ignored`);
        return false;
    }
    // A Skeleton3D with no root bone in this GLB has no group to move.
    if (!frame.group)
        return false;
    writeNodeTransform(frame.group, decomposeForR3F(transform));
    return true;
}
/**
 * What hiding `frame` hides: its group, which holds the root bones or what hangs on the bone, and
 * every object that stands for a node below it, such as a skinned mesh the group does not hold.
 */
function objectsBelowFrame(entries, frame) {
    const nodes = entries
        .filter(({ object }) => godotNodePaths(object).some((path) => relativeTo(frame.path, path) !== undefined))
        .map(({ object }) => object);
    return frame.group ? [frame.group, ...nodes] : nodes;
}
/**
 * Each Skeleton3D and BoneAttachment3D of the GLB with the objects below it. The GLB root hides
 * them in turn, since a frame's `visible` and the `visible` of a node below it both apply.
 *
 * @canonical The Skeleton3D and BoneAttachment3D groups of an instanced GLB, with what each hides
 */
export function frameMembers(entries) {
    return glbFrames(entries).map((frame) => ({ frame, below: objectsBelowFrame(entries, frame) }));
}
function warnUnreadProperties(entries, frame, override) {
    for (const key of Object.keys(override.rawProperties)) {
        if (!UNREAD_PROPERTIES[frame.kind].some((name) => name.endsWith('/') ? key.startsWith(name) : key === name)) {
            continue;
        }
        warnOnce(entries, `${frame.path}\0${key}`, `[glbFrameOverrides] "${frame.path}" sets ${key}, which the previewer does not drive yet (#980): it is ignored`);
    }
}
//# sourceMappingURL=glbFrameOverrides.js.map